import type { KeyboardEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/free-solid-svg-icons';
import styles from '../FeedbackPanel.module.scss?module';

interface StarRatingProps {
  /** Chosen stars, 0 for none yet. */
  value: number;
  max: number;
  /** Id of the question text that labels the group. */
  labelledBy: string;
  invalid?: boolean;
  onChange: (value: number) => void;
}

/**
 * Star rating as a radio group: click or Space picks a star, arrow keys move between the stars,
 * so it works without the unmaintained react-stars of the old popup.
 */
export default function StarRating({
  value,
  max,
  labelledBy,
  invalid = false,
  onChange,
}: StarRatingProps) {
  const stars = Array.from({ length: max }, (_, index) => index + 1);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    let next: number | null = null;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        next = Math.min(max, value + 1);
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        next = Math.max(1, value - 1);
        break;
      case 'Home':
        next = 1;
        break;
      case 'End':
        next = max;
        break;
    }
    if (next === null) {
      return;
    }
    event.preventDefault();
    onChange(next);
    const button = event.currentTarget.querySelector<HTMLButtonElement>(
      `[data-star="${next}"]`
    );
    button?.focus();
  };

  return (
    <div
      className={styles.stars}
      role="radiogroup"
      aria-labelledby={labelledBy}
      aria-invalid={invalid || undefined}
      onKeyDown={handleKeyDown}
    >
      {stars.map((star) => {
        const checked = star === value;
        // Only one star is in the tab order: the chosen one, or the first without a choice.
        const focusable = checked || (value === 0 && star === 1);

        return (
          <button
            key={star}
            type="button"
            role="radio"
            data-star={star}
            data-filled={star <= value}
            aria-checked={checked}
            aria-label={`${star} of ${max} stars`}
            tabIndex={focusable ? 0 : -1}
            className={styles.star}
            onClick={() => onChange(star)}
          >
            <FontAwesomeIcon icon={faStar} />
          </button>
        );
      })}
    </div>
  );
}
